<script setup lang="ts">
import { ArrowRight } from '@element-plus/icons-vue'
import callservice from './callservice.vue'
import {
  type CustomerPhoneItem,
  getCustomerContactSite,
  getOtherSitePhoneConfigs,
  normalizeCustomerContacts,
  normalizeSitePhoneConfigs,
} from './customerPhoneAdapter'
import { http } from '@/utils/http'
import { getSiteCode } from '@/utils/util'
import { customersEvent } from '~/utils/track/customer'
import { seaWhaleApiUserQueryPost } from '~/services/api/MemberApi'
import {
  seaTurtleApiCustomerConfigAllPost,
  seaTurtleApiCustomerContactDetailPost,
} from '~/services/api/SiteApi'

const { t } = useLang()
const { locale } = useI18n()
const requestUrl = useRequestURL()
const runtimeConfig = useRuntimeConfig()

function getCanonicalOrigin() {
  const envOrigin = runtimeConfig.public.envData.VITE_API_HOST || ''
  return /^https?:\/\//.test(envOrigin) ? envOrigin.replace(/\/$/, '') : requestUrl.origin
}

const customerDesktopUrl = computed(() => `${getCanonicalOrigin()}/${locale.value}/common/customer`)
const customerMobileUrl = computed(() => `${getCanonicalOrigin()}/m/${locale.value}/customer`)

const postLoading = ref(true)
const isJaLocale = computed(() => locale.value.toLowerCase() === 'ja-jp')

definePageMeta({
  layout: 'immersive',
})

useHead(() => ({
  title: t('csEmail.feed_share_your_questions_and_feedback'),
  meta: [
    { name: 'keywords', content: t('common.b_tdk_keywords') },
    { name: 'description', content: t('common.b_tdk_description') },
    { name: 'author', content: t('common.b_author') },
    { name: 'copyright', content: t('common.b_company') },
  ],
  link: [
    { key: 'canonical', rel: 'canonical', href: customerDesktopUrl.value },
    {
      key: 'mobile-alternate',
      rel: 'alternate',
      media: 'only screen and (max-width: 640px)',
      href: customerMobileUrl.value,
    },
  ],
  script: [
    {
      src: '/intlPC/js/jquery-3.3.1.min.js',
      type: 'text/javascript',
    },
    /* 风控sdk #s */
    {
      src: `//file.40017.cn/ysrc/duhurisk.min.js?v=${$utils.getCurrentDateTime()}`,
      tagPosition: 'bodyClose',
      async: true,
    },
    {
      src: '//jy.17u.cn/recaptcha/api.js?project_id=en_ly_com&timestamp=12223',
      tagPosition: 'bodyClose',
      defer: true,
    },
    /* 风控sdk #end */
  ],
}))
const customerContacts = ref<CustomerPhoneItem[]>([])
const sitePhoneConfigs = ref<ReturnType<typeof normalizeSitePhoneConfigs>>([])
const customerContactSite = ref('')
const showCallService = ref(false)
const showCallBtn = computed(() => customerContacts.value.length > 0)
const hasNormalPhone = computed(() =>
  customerContacts.value.some(item => item.configType !== 1),
)
const showOtherRegions = computed(() => {
  return hasNormalPhone.value
    && getOtherSitePhoneConfigs(
      sitePhoneConfigs.value,
      customerContactSite.value || getSiteCode(),
    ).length > 0
})
const baseContactInfos = [
  {
    title: t('customer.p_customer_card_title_chat'),
    content: t('customer.p_customer_card_content_chat'),
    buttonText: t('customer.p_customer_card_button_chat'),
    action: 'chat',
    icon: 'question_online.png',
  },
  {
    title: t('customer.p_customer_card_title_phone'),
    content: t('customer.p_customer_card_content_phone'),
    buttonText: t('customer.p_customer_card_button_phone'),
    action: 'call',
    icon: 'question_host.png',
  },
  {
    title: t('customer.p_customer_card_title_email'),
    content: t('customer.p_customer_card_content_email'),
    buttonText: t('customer.p_customer_card_button_email'),
    action: 'go',
    icon: 'question_mail.png',
  },
]

const contactInfos = computed(() => {
  const channelItems = isJaLocale.value
    ? baseContactInfos.filter(item => item.action !== 'chat')
    : baseContactInfos
  return channelItems.filter(item => item.action !== 'call' || showCallBtn.value)
})

async function getCustomerPhoneData() {
  const [contactResult, configResult] = await Promise.allSettled([
    seaTurtleApiCustomerContactDetailPost({}),
    seaTurtleApiCustomerConfigAllPost({}),
  ])

  customerContacts.value = contactResult.status === 'fulfilled'
    ? normalizeCustomerContacts(contactResult.value, t('customer.p_customer_phone_call'))
    : []
  customerContactSite.value = contactResult.status === 'fulfilled'
    ? getCustomerContactSite(contactResult.value)
    : ''
  sitePhoneConfigs.value = configResult.status === 'fulfilled'
    ? normalizeSitePhoneConfigs(configResult.value)
    : []
}
const userInfo = ref<any>({})

onMounted(async () => {
  getCategoryList()
  await getCustomerPhoneData()
  // contactInfos
  const servicetypes = contactInfos.value.map((item: any) => {
    // if (item.action === 'call') { return 'call' }
    if (item.action === 'go') { return 'feedback' }
    if (item.action === 'chat') { return 'online' }
    return item.action
  })
  customersEvent.pageShowEvent({ servicetypes: servicetypes.join(';') })

  seaWhaleApiUserQueryPost({}).then((res) => {
    if (res) {
      userInfo.value = res
    }
  })
})
const categoryList = ref<{ categoryId: number; categoryName: string }[]>([])

const cnCategoryId = ref(0)
const questions = ref<any[]>([])
const secondCategories = ref<any[]>([])

function getfaqList(needToast = false) {
  http({
    url: '/sea_turtle_api/helpCenter/qaListByFirstCategory',
    baseURL: '/sea_shell',
    method: 'post',
    body: {
      firstCategoryId: cnCategoryId.value,
      platFormNo: 1,
    },
  }).then((res: any) => {
    const { faqQuestionDtos, secondCategoryDtos } = res.body
    questions.value = faqQuestionDtos
    secondCategories.value = secondCategoryDtos
  }).catch(() => {
    needToast && $utils.messageBox(t, t('csEmail.feed_enter_cant_find_order'))
  }).finally(() => {
  })
}

function changeFAQtype(category: { categoryId: number; categoryName: string }) {
  cnCategoryId.value = category.categoryId
  customersEvent.clickEvent('FAQ', category.categoryName, '')
}
// 筛选功能处理
const showFilterContent = ref(false)

function closeFilterEvent() {
  showFilterContent.value = false
}

function getCategoryList() {
  postLoading.value = true
  http({
    url: '/sea_turtle_api/helpCenter/firstCategoryList',
    baseURL: '/sea_shell',
    method: 'post',
    body: {
      platFormNo: 1,
    },
  }).then((res: any) => {
    categoryList.value = res.body
    changeFAQtype(categoryList.value[0])
  }).catch((e) => {
    console.log(e)
  }).finally(() => {
    postLoading.value = false
  })
}
watch(cnCategoryId, (val) => {
  getfaqList()
})

function useAsset(path: string): string {
  const assets = import.meta.glob('~/assets/images/*', {
    eager: true,
    import: 'default',
  })
  // @ts-expect-error: wrong type info
  return assets[`/assets/images/${path}`]
}
// Usage: <img :src="useAsset(dynamic_image_name + '.svg')" alt="Discover Nuxt 3" />

function goContact(info: any) {
  if (info.action === 'go') {
    location.href = `/${locale.value}/common/email-us`
    customersEvent.clickEvent('CustomerService', 'Email', '')
  }
  else if (info.action === 'chat') {
    const nav = document.querySelector('button.ihome-common-nav') as HTMLButtonElement
    nav && nav.click()
    customersEvent.clickEvent('CustomerService', 'Chat', '')

    customersEvent.chatFloatShowEvent()
  }
  else if (info.action === 'call') {
    showCallService.value = true
    // const callbtn = document.querySelector('#icall-common-nav') as HTMLButtonElement
    // callbtn && callbtn.click()
    customersEvent.clickEvent('CustomerService', 'Phone', '')
    const hasPhoneNumber = customerContacts.value.some(item => item.configType !== 1)
    const hasVoip = customerContacts.value.some(item => item.configType === 1)
    const type = [hasPhoneNumber ? 'phonenumber' : '', hasVoip ? 'voip' : ''].filter(Boolean).join(';')
    customersEvent.callFloatShowEvent({
      type,
      isOtherNumber: showOtherRegions.value ? 'yes' : 'no',
    })
  }
}

function goOtherRegions() {
  showCallService.value = false
  customersEvent.callFloatClickEvent('OtherNumber')
  navigateTo(`/${locale.value}/common/customer/other-regions`)
}
function goQuestion(item: any) {
  location.href = `/${locale.value}/common/faq/${item.id}`
  customersEvent.clickEvent(
    'FAQ',
    categoryList.value.find(i => i.categoryId === cnCategoryId.value)?.categoryName || '',
    'Question',
  )
}
function goCategory(category: any) {
  location.href = `/${locale.value}/common/faq/?cookie=1`

  useCookie<any>('faq').value = {
    secondCategoryId: category.id,
    categoryId: cnCategoryId.value,
  }
  customersEvent.clickEvent(
    'FAQ',
    categoryList.value.find(i => i.categoryId === cnCategoryId.value)?.categoryName || '',
    category.name,
  )
}
function goFaq() {
  location.href = `/${locale.value}/common/faq/?cookie=1`

  useCookie<any>('faq').value = {
    categoryId: cnCategoryId.value,
  }
  customersEvent.clickEvent(
    'FAQ',
    categoryList.value.find(i => i.categoryId === cnCategoryId.value)?.categoryName || '',
    '...',
  )
}
</script>

<template>
  <div class="maincontent" @click="closeFilterEvent">
    <div class="main-title">
      <div class="main-title-box">
        <div>
          <h1 class="m-0 big-title leading-[1.4]">
            {{ t('customer.p_customer_title_hello') }}
          </h1>
          <span class="small-title !mt-1">{{ t('customer.p_customer_subtitle_avaliable_time') }}</span>
        </div>
        <img src="//file.reshg.com/hopegoo/web-pc/images/common/img/icon-e-top.png" alt="">
      </div>
    </div>
    <div v-if="!postLoading" class="content">
      <div v-if="categoryList?.length" class="sel-box">
        <div
          v-for="(item) in categoryList" :key="item.categoryId"
          :class="cnCategoryId === item.categoryId ? 'business-item active' : 'business-item'"
          @click="changeFAQtype(item)"
        >
          <span class="truncate max-w-[220px]"> {{ item.categoryName }}</span>
        </div>
      </div>
      <div class="items">
        <div v-for="question in questions" :key="question.id" class="item" @click="goQuestion(question)">
          {{ question.questionTitle }}
          <el-icon><ArrowRight /></el-icon>
        </div>
      </div>
      <div v-if="secondCategories?.length" class="content-card">
        <div class="content-title">
          <span> {{ t('customer.p_customer_category_title_faq') }}</span>
        </div>
        <div class="content-body faq-content">
          <div v-for="category in secondCategories" :key="category.id" class="faq-tab" @click="goCategory(category)">
            {{ category.name }}
          </div>
          <div class="faq-tab" @click="goFaq">
            ...
          </div>
        </div>
      </div>
      <div class="content-card">
        <div class="content-title">
          <span> {{ t('customer.p_customer_category_title_contact') }}
          </span>
        </div>
        <div class="content-body">
          <div v-for="info in contactInfos" :key="info.title" class="contact-card" @click="goContact(info)">
            <img :src="useAsset(`${info.icon}`)" alt="">
            <div class="">
              <!-- max-w-[280px] -->
              <div class="main truncate">
                {{ info.title }}
              </div>
              <div class="sub line-clamp-2">
                {{ info.buttonText }}
                {{ info.content }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="loading-state">
      <div><img src="@/assets/img/loading.gif" alt=""></div>
    </div>
    <callservice
      v-model="showCallService"
      :call-info="customerContacts"
      :show-other-regions="showOtherRegions"
      :user-info="userInfo"
      @other="goOtherRegions"
    />
  </div>
</template>

<style scoped lang="scss">
.loading-state{
  width: 100%;
  padding-top: 120px;
  display: flex;
  justify-content:center;
  align-items: center;
}
.content-card{
    margin-top: 32px;
    .content-title{
        font-size: 24px;
        font-weight: bold;
        color: #000;
    }
    .content-body{
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;

        &.faq-content {
          margin-top: 0;
        }

        &.faq-content {
          .faq-tab {
            margin-top: 16px;
          }
        }

        .faq-tab{
          cursor: pointer;
            display: flex;
            height: 48px;
            padding: 8px 16px;
            justify-content: center;
            align-items: center;
            margin-right: 8px;
            border-radius: 4px;
            background: var(---Fill-Gray, #F3F3F3);
            font-size: 16px;
            &:hover{
              color: var(--Brand-Green);
            }
        }
        .contact-card{
          border-radius: 4px;
          user-select: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          height: 128px;
          padding: 27px 24px;
          border: 1px solid #e4e4e4;
          flex:1;
          &:hover{
            box-shadow:  0px 4px 8px rgba(0, 0, 0, 0.08);
          }
          &:not(:last-child){
            margin-right: 8px;
          }
          >img{
            width: 64px;
            height: 64px;
            margin-right: 16px;
          }
          .main{
            font-weight: bold;
            font-size: 18px;
            color: #000;
          }
          .sub{
            font-size: 14px;
            color: #666;
            margin-top: 8px;
          }
        }
    }
}
.font-segoe {
    font-size: 24px;
    font-weight: bold;
    font-family: Segoe UI, Helvetica, Arial, sans-serif;
}

.maincontent .el-button {
    color: #000;
    border-radius: 2px;
    background: var(--Brand-Green);
    border: none;
}

.dia-content {
    // padding: 24px 32px 44px;
    @extend .font-segoe;

    .title {
        @extend .font-segoe;
        font-weight: bold;
        color: var(--Brand-Green);
        margin-bottom: 16px;
    }

    .tips {
        @extend .font-segoe;
        font-size: 16px;
        font-weight: normal;
        color: #333;
        margin-bottom: 24px;
    }

    .input-box {
        margin-bottom: 16px;

        .label {
            font-size: 16px;
            font-weight: normal;
            color: #333;
            margin-bottom: 8px;

            :not(:last-child) {
                color: #F00;
            }
        }

        .dia-input {
            height: 48px;
            width: 100%;
            border-radius: 4px;
            background: #f3f3f3;
            padding: 13px 16px;
            color: #333;
            font-weight: normal;
            font-size: 16px;
            border: 1px solid #f3f3f3;

            &.err {
                border: 1px solid var(--Warning-Red);
                color: var(--Warning-Red);
            }

            &.focus {
                border: 1px solid var(--Brand-Green);
            }
        }

        .vaild-err {
            @extend .font-segoe;
            color: var(--Warning-Red);
            font-weight: normal;
            font-size: 14px;
            margin-top: 4px;
        }

    }

    .dia-btn {
        width: 100%;
        height: 48px;
        background-color: var(--Brand-Green);
        color: #fff;
        font-weight: bold;
        cursor: pointer;
        margin-top: 24px;
        border-radius: 4px;

        &:hover {
            background: var(--Click-Green);
        }

        &.disabled {
            cursor: not-allowed;
            opacity: 0.5;

            &:hover {
                background-color: var(--Brand-Green);
            }
        }

        div {
            text-align: center;
            line-height: 48px;

        }

        .loading {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: not-allowed;

            div {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #000;
                margin-right: 24px;

                &.op30 {
                    opacity: 0.3;
                }

                &.op70 {
                    opacity: 0.7;
                }
            }
        }
    }
}

input,
textarea {
    vertical-align: middle;
    font-size: 100%;
    outline: 0;
    border: none;
    font-family: Segoe UI, Helvetica, Arial, sans-serif;
    caret-color: var(--Brand-Green);

    &:focus {
        outline: 1px solid var(---Brand-Green, #00C868);
    }
}

.main-title {
    font-size: 24px;
    height: 160px;
    color: #fff;
    font-weight: bold;
    font-family: Segoe UI, Helvetica, Arial, sans-serif;
    margin-bottom: 32px;
    background: var(--Brand-Green);
    margin-top: -20px;
    line-height: 1;

    .main-title-box {
        width: 1190px;
        margin: 0 auto 60px auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 40px;
        font-weight: 900;
    }

    .big-titile {
        font-size: 40px
    }

    .small-title {
        font-size: 20px;
    }

    span {
        display: block;
        margin: 0 auto;
    }
}

.tips-title {
    color: #000;
    font-family: "Segoe UI";
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
}

.tips-text {
    color: #717171;
    font-family: "Segoe UI";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.content {
    width: 1190px;
    min-height: 600px;
    margin: 0 auto 60px auto;

    .sel-box {
        display: flex;
        align-items: center;
        margin-bottom: 14px;
        display: flex;
        flex-wrap: wrap;
        min-height: 48px;
        height: auto;
        align-items: center;
        margin-bottom: 14px;

        .business-item {
            cursor: pointer;
            font-size: 18px;
            @extend .font-segoe;
            padding: 0 40px;
            position: relative;
            color: #000;
            height: 48px;
            display: flex;
            align-items: flex-end;

            &.active {
                font-size: 24px;
                font-weight: bold;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: -4px;
                    width: 25px;
                    height: 3px;
                    background: var(--Brand-Green);
                    right: 50%;
                    border-radius: 28px;
                    transform: translateX(50%);
                }
            }

            &::before {
                content: '';
                height: 16px;
                width: 1px;
                border: 1px solid var(--Line);
                position: absolute;
                left: 0;
                bottom: 10px;
            }

            &:first-child {
                margin-left: -40px;

                &::before {
                    display: none;
                }
            }
        }
    }
    .items{
        display: flex;
        flex-wrap: wrap;
        margin-top: 20px;
        .item{
            display: flex;
            align-items: center;
            font-size: 16px;
            width:calc(50% - 8px);
            height: 60px;
            background: #f3f3f3;
            border-radius: 4px;
            margin-bottom: 8px;
            padding:0 16px;
            color:#000;
            margin-right: 8px;
            justify-content: space-between;
            &:hover{
              color: var(--Brand-Green);
            }
        }
    }
}
</style>
